import React, { useRef, useEffect, useState } from 'react';
import { 
  Box, 
  Paper, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { DeviceSize } from './DeviceControls';
import { PreviewResult } from '../types';

interface PreviewPanelProps {
  html: string;
  clientType: 'gmail' | 'outlook' | 'apple' | 'yahoo' | 'thunderbird' | 'plaintext';
  isPro?: boolean;
  onClientChange: (client: 'gmail' | 'outlook' | 'apple' | 'yahoo' | 'thunderbird' | 'plaintext') => void;
  deviceSize: DeviceSize;
  previewResults?: PreviewResult[];
  isLoading?: boolean;
}

interface ClientWarning {
  warning: string;
  severity: 'error' | 'warning';
}

interface ClientQuirks {
  imageDisplay?: string;
  maxWidth?: string;
  lineHeight?: string;
  tableLayout?: string;
  msoTableLspace?: string;
  msoTableRspace?: string;
  webkitTextSizeAdjust?: string;
  webkitFontSmoothing?: string;
  overflowWrap?: string;
  wordWrap?: string;
  mozBoxSizing?: string;
  cssTransforms?: boolean;
  supportsWebFonts?: boolean;
  supportsMediaQueries?: boolean;
  hasImageAltBug?: boolean;
  hasLineHeightBug?: boolean;
  needsTableFix?: boolean;
  supportsDarkMode?: boolean;
  supportsResponsive?: boolean;
  supportsAnimations?: boolean;
  supportsEmoji?: boolean;
  supportsHtml5?: boolean;
  supportsInteractivity?: boolean;
}

interface ClientStyle {
  fontFamily: string;
  maxWidth: string;
  margin: string;
  backgroundColor: string;
  darkMode: {
    backgroundColor: string;
    color: string;
  };
  quirks: ClientQuirks;
  warningChecks?: (html: string) => ClientWarning[];
}

const clientStyles: Record<string, ClientStyle> = {
  gmail: {
    fontFamily: 'Arial, sans-serif',
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    darkMode: {
      backgroundColor: '#2b2b2b',
      color: '#ffffff',
    },
    quirks: {
      imageDisplay: 'block',
      maxWidth: '100%',
      lineHeight: '1.5',
      cssTransforms: true,
      supportsWebFonts: false,
      supportsMediaQueries: true,
      hasImageAltBug: false,
      hasLineHeightBug: false,
      needsTableFix: false,
      supportsDarkMode: true,
      supportsResponsive: true,
      supportsAnimations: false,
      supportsEmoji: true,
      supportsHtml5: false,
      supportsInteractivity: false
    },
    warningChecks: (html: string): ClientWarning[] => {
      const warnings: ClientWarning[] = [];
      
      // Check for unsupported web fonts
      const fontFaces = html.match(/@font-face/g);
      if (fontFaces) {
        warnings.push({
          warning: 'Gmail does not support web fonts (@font-face). Use web-safe fonts instead.',
          severity: 'error' as const
        });
      }

      // Check for background images
      if (html.includes('background-image')) {
        warnings.push({
          warning: 'Gmail may not support background images consistently.',
          severity: 'warning' as const
        });
      }

      // Check for CSS that Gmail strips
      const cssProperties = [
        'position:', 
        'float:', 
        'clear:', 
        'z-index:', 
        'display: none',
        'margin:', // Gmail sometimes strips margin
        'visibility:',
        'top:',
        'left:',
        'bottom:',
        'right:',
        'cursor:',
        'opacity:',
        'transform:',
        'transition:'
      ];
      cssProperties.forEach(prop => {
        if (html.includes(prop)) {
          warnings.push({
            warning: `Gmail strips "${prop}" CSS property.`,
            severity: 'error' as const
          });
        }
      });

      // Check for responsive design patterns
      if (html.includes('@media') && !html.includes('gmail.com')) {
        warnings.push({
          warning: 'Gmail requires @media queries to include ".gmail.com" in the selector for better support.',
          severity: 'warning' as const
        });
      }

      // Check for HTML5 elements
      const html5Elements = ['article', 'aside', 'details', 'figcaption', 'figure', 'footer', 'header', 'main', 'mark', 'nav', 'section', 'summary', 'time'];
      html5Elements.forEach(element => {
        if (html.includes(`<${element}`)) {
          warnings.push({
            warning: `Gmail has limited support for the HTML5 <${element}> element.`,
            severity: 'warning' as const
          });
        }
      });

      // Check for embedded styles
      if (html.includes('<style>') && !html.includes('<style>.gmail-fix')) {
        warnings.push({
          warning: 'Gmail may not support embedded styles consistently. Consider using inline styles.',
          severity: 'warning' as const
        });
      }

      return warnings;
    }
  },
  outlook: {
    fontFamily: 'Calibri, Arial, sans-serif',
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    darkMode: {
      backgroundColor: '#1f1f1f',
      color: '#ffffff',
    },
    quirks: {
      tableLayout: 'fixed',
      msoTableLspace: '0pt',
      msoTableRspace: '0pt',
      cssTransforms: false,
      supportsWebFonts: false,
      supportsMediaQueries: false,
      hasImageAltBug: true,
      hasLineHeightBug: true,
      needsTableFix: true,
      supportsDarkMode: false,
      supportsResponsive: false,
      supportsAnimations: false,
      supportsEmoji: true,
      supportsHtml5: false,
      supportsInteractivity: false
    },
    warningChecks: (html: string): ClientWarning[] => {
      const warnings: ClientWarning[] = [];
      
      // Check for padding on divs
      if (html.includes('div') && html.includes('padding')) {
        warnings.push({
          warning: 'Outlook does not support padding on div elements. Use tables instead.',
          severity: 'error' as const
        });
      }

      // Check for max-width
      if (html.includes('max-width')) {
        warnings.push({
          warning: 'Outlook does not support max-width. Use fixed widths for tables.',
          severity: 'error' as const
        });
      }

      // Check for missing MSO conditional comments
      if (!html.includes('<!--[if mso]>')) {
        warnings.push({
          warning: 'Consider adding MSO conditional comments for better Outlook compatibility.',
          severity: 'warning' as const
        });
      }

      // Check for unsupported CSS properties
      const outlookUnsupportedCSS = [
        'border-radius:',
        'box-shadow:',
        'text-shadow:',
        'transform:',
        'transition:',
        'background-size:',
        'flex',
        'grid',
        'opacity:',
        'overflow:',
        'position: absolute',
        'position: relative',
        'position: fixed'
      ];

      outlookUnsupportedCSS.forEach(prop => {
        if (html.includes(prop)) {
          warnings.push({
            warning: `Outlook does not support "${prop.replace(':', '')}" CSS property.`,
            severity: 'error' as const
          });
        }
      });

      // Check for VML fallback for background images
      if (html.includes('background-image') && !html.includes('v:background')) {
        warnings.push({
          warning: 'Use VML fallback for background images in Outlook.',
          severity: 'warning' as const
        });
      }

      // Check for table structure
      if (!html.includes('role="presentation"')) {
        warnings.push({
          warning: 'Add role="presentation" to tables for better Outlook compatibility.',
          severity: 'warning' as const
        });
      }

      // Check for line-height on images
      if (html.includes('<img') && html.includes('line-height')) {
        warnings.push({
          warning: 'Outlook has issues with line-height on images. Use height attribute instead.',
          severity: 'error' as const
        });
      }

      return warnings;
    }
  },
  apple: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif',
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    darkMode: {
      backgroundColor: '#1c1c1e',
      color: '#ffffff',
    },
    quirks: {
      webkitTextSizeAdjust: '100%',
      webkitFontSmoothing: 'antialiased',
      cssTransforms: true,
      supportsWebFonts: true,
      supportsMediaQueries: true,
      hasImageAltBug: false,
      hasLineHeightBug: false,
      needsTableFix: false,
      supportsDarkMode: true,
      supportsResponsive: true,
      supportsAnimations: true,
      supportsEmoji: true,
      supportsHtml5: true,
      supportsInteractivity: true
    },
    warningChecks: (html: string): ClientWarning[] => {
      const warnings: ClientWarning[] = [];
      
      // Check for Windows-specific fonts
      if (html.includes('Calibri') || html.includes('Segoe UI')) {
        warnings.push({
          warning: 'Windows-specific fonts may not render correctly in Apple Mail. Consider using system fonts.',
          severity: 'warning'
        });
      }

      // Check for Outlook-specific CSS
      if (html.includes('mso-')) {
        warnings.push({
          warning: 'Outlook-specific "mso-" styles are not needed for Apple Mail and may cause issues.',
          severity: 'warning'
        });
      }

      return warnings;
    }
  },
  yahoo: {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    darkMode: {
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
    },
    quirks: {
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      cssTransforms: true,
      supportsWebFonts: false,
      supportsMediaQueries: true,
      hasImageAltBug: false,
      hasLineHeightBug: false,
      needsTableFix: true,
      supportsDarkMode: true,
      supportsResponsive: true,
      supportsAnimations: false,
      supportsEmoji: true,
      supportsHtml5: false,
      supportsInteractivity: false
    },
    warningChecks: (html: string): ClientWarning[] => {
      const warnings: ClientWarning[] = [];
      
      // Check for web fonts
      if (html.includes('@font-face')) {
        warnings.push({
          warning: 'Yahoo Mail does not support web fonts. Use web-safe fonts instead.',
          severity: 'error'
        });
      }

      // Check for complex CSS selectors
      if (html.includes('>') || html.includes('+') || html.includes('~')) {
        warnings.push({
          warning: 'Yahoo Mail has limited support for complex CSS selectors.',
          severity: 'warning'
        });
      }

      // Check for embedded styles
      if (html.includes('<style>')) {
        warnings.push({
          warning: 'Yahoo Mail may strip embedded styles. Use inline styles for better compatibility.',
          severity: 'warning'
        });
      }

      return warnings;
    }
  },
  thunderbird: {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    darkMode: {
      backgroundColor: '#2a2a2e',
      color: '#ffffff',
    },
    quirks: {
      mozBoxSizing: 'border-box',
      cssTransforms: true,
      supportsWebFonts: true,
      supportsMediaQueries: true,
      hasImageAltBug: false,
      hasLineHeightBug: false,
      needsTableFix: false,
      supportsDarkMode: true,
      supportsResponsive: true,
      supportsAnimations: true,
      supportsEmoji: true,
      supportsHtml5: true,
      supportsInteractivity: true
    },
    warningChecks: (html: string): ClientWarning[] => {
      const warnings: ClientWarning[] = [];
      
      // Check for Outlook-specific markup
      if (html.includes('<!--[if')) {
        warnings.push({
          warning: 'Conditional comments are not needed for Thunderbird and may cause display issues.',
          severity: 'warning'
        });
      }

      // Check for Exchange-specific headers
      if (html.includes('x-exchange')) {
        warnings.push({
          warning: 'Exchange-specific headers are not needed for Thunderbird.',
          severity: 'warning'
        });
      }

      return warnings;
    }
  },
  plaintext: {
    fontFamily: 'monospace',
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    darkMode: {
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
    },
    quirks: {
      cssTransforms: false,
      supportsWebFonts: false,
      supportsMediaQueries: false,
      hasImageAltBug: false,
      hasLineHeightBug: false,
      needsTableFix: false,
      supportsDarkMode: true,
      supportsResponsive: false,
      supportsAnimations: false,
      supportsEmoji: false,
      supportsHtml5: false,
      supportsInteractivity: false
    },
    warningChecks: (html: string): ClientWarning[] => {
      const warnings: ClientWarning[] = [];
      
      // Check for HTML elements that will be stripped
      const htmlElements = ['<div', '<span', '<p', '<table', '<img', '<a'];
      htmlElements.forEach(element => {
        if (html.includes(element)) {
          warnings.push({
            warning: `${element}> tags will be stripped in plaintext view.`,
            severity: 'warning'
          });
        }
      });

      // Check for styled content
      if (html.includes('style=') || html.includes('<style')) {
        warnings.push({
          warning: 'All styling will be removed in plaintext view.',
          severity: 'warning'
        });
      }

      return warnings;
    }
  }
};

export { clientStyles };

export const PreviewPanel: React.FC<PreviewPanelProps> = ({ 
  html, 
  clientType, 
  isPro = false, 
  onClientChange, 
  deviceSize,
  previewResults = [],
  isLoading = false
}) => {
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('light');
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number>();
  const [warnings, setWarnings] = React.useState<ClientWarning[]>([]);

  useEffect(() => {
    // Calculate scale to fit the preview container
    const container = containerRef.current;
    if (container) {
      const containerWidth = container.offsetWidth;
      const deviceWidth = deviceSize.width;
      const newScale = Math.min(1, (containerWidth - 40) / deviceWidth); // 40px for padding
      setScale(newScale);
    }
  }, [deviceSize.width]);

  useEffect(() => {
    // Check for warnings when html or clientType changes
    const style = clientStyles[clientType];
    if (style?.warningChecks) {
      const newWarnings = style.warningChecks(html);
      setWarnings(newWarnings);
    } else {
      setWarnings([]);
    }
  }, [html, clientType]);

  const handleClientChange = (event: SelectChangeEvent) => {
    onClientChange(event.target.value as typeof clientType);
  };

  const handleColorSchemeChange = (_: React.MouseEvent<HTMLElement>, newScheme: 'light' | 'dark' | null) => {
    if (newScheme !== null) {
      setColorScheme(newScheme);
    }
  };

  const transformHtmlForClient = (html: string, client: string): string => {
    if (!html) return '';
    
    if (client === 'plaintext') {
      return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/^\s+|\s+$/g, '')
        .split('\n').map(line => line.trim()).join('\n');
    }

    const style = clientStyles[client];
    if (!style) return html;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    // Apply client-specific transformations
    if (style.quirks.needsTableFix) {
      wrapper.querySelectorAll('table').forEach(table => {
        table.setAttribute('cellspacing', '0');
        table.setAttribute('cellpadding', '0');
        table.setAttribute('border', '0');
      });
    }

    if (!style.quirks.supportsWebFonts) {
      const styleElements = wrapper.getElementsByTagName('style');
      Array.from(styleElements).forEach(styleEl => {
        styleEl.innerHTML = styleEl.innerHTML.replace(/@font-face\s*{[^}]*}/g, '');
      });
    }

    if (!style.quirks.supportsMediaQueries) {
      const styleElements = wrapper.getElementsByTagName('style');
      Array.from(styleElements).forEach(styleEl => {
        styleEl.innerHTML = styleEl.innerHTML.replace(/@media[^{]+{[\s\S]+?}\s*}/g, '');
      });
    }

    return `
      <div class="email-client ${client}" style="
        font-family: ${style.fontFamily};
        max-width: 100%;
        margin: 0;
        padding: 0;
        background-color: ${colorScheme === 'dark' ? style.darkMode?.backgroundColor : style.backgroundColor};
        color: ${colorScheme === 'dark' ? style.darkMode?.color : 'inherit'};
        ${style.quirks.webkitTextSizeAdjust ? `webkit-text-size-adjust: ${style.quirks.webkitTextSizeAdjust};` : ''}
        ${style.quirks.webkitFontSmoothing ? `webkit-font-smoothing: ${style.quirks.webkitFontSmoothing};` : ''}
        ${style.quirks.overflowWrap ? `overflow-wrap: ${style.quirks.overflowWrap};` : ''}
        ${style.quirks.wordWrap ? `word-wrap: ${style.quirks.wordWrap};` : ''}
        ${style.quirks.mozBoxSizing ? `moz-box-sizing: ${style.quirks.mozBoxSizing};` : ''}
      ">
        ${wrapper.innerHTML}
      </div>
    `;
  };

  // Get current preview result for selected client
  const currentPreview = previewResults.find(p => p.clientName.toLowerCase() === clientType);
  
  // Combine warnings from preview service with client-specific warnings
  const allWarnings = [
    ...(currentPreview?.warnings || []).map(w => ({
      warning: w.message,
      severity: w.severity === 'high' ? 'error' as const : 'warning' as const
    })),
    ...warnings
  ];

  return (
    <Paper elevation={0} sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      borderRadius: 2
    }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', mb: allWarnings.length > 0 ? 2 : 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Email Client</InputLabel>
            <Select
              value={clientType}
              label="Email Client"
              onChange={handleClientChange}
            >
              <MenuItem value="gmail">Gmail {currentPreview && `(${Math.round(currentPreview.confidence * 100)}% compatible)`}</MenuItem>
              <MenuItem value="outlook">Outlook {currentPreview && `(${Math.round(currentPreview.confidence * 100)}% compatible)`}</MenuItem>
              <MenuItem value="apple" disabled={!isPro}>
                Apple Mail {!isPro ? '(Pro)' : currentPreview && `(${Math.round(currentPreview.confidence * 100)}% compatible)`}
              </MenuItem>
              <MenuItem value="yahoo" disabled={!isPro}>
                Yahoo Mail {!isPro ? '(Pro)' : currentPreview && `(${Math.round(currentPreview.confidence * 100)}% compatible)`}
              </MenuItem>
              <MenuItem value="thunderbird" disabled={!isPro}>
                Thunderbird {!isPro ? '(Pro)' : currentPreview && `(${Math.round(currentPreview.confidence * 100)}% compatible)`}
              </MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={colorScheme}
            exclusive
            onChange={handleColorSchemeChange}
            aria-label="color scheme"
            size="small"
          >
            <ToggleButton value="light" aria-label="light mode">
              <Tooltip title="Light Mode">
                <LightModeIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="dark" aria-label="dark mode">
              <Tooltip title="Dark Mode">
                <DarkModeIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {allWarnings.length > 0 && (
          <Box sx={{ width: '100%' }}>
            {allWarnings.map((warning, index) => (
              <Alert 
                key={index} 
                severity={warning.severity}
                sx={{ mb: index < allWarnings.length - 1 ? 1 : 0 }}
                variant="outlined"
              >
                {warning.warning}
              </Alert>
            ))}
          </Box>
        )}
      </Box>
      
      <Box 
        ref={containerRef}
        sx={{ 
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: theme => theme.palette.grey[100],
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 2
        }}
      >
        <Box
          sx={{
            width: `${deviceSize.width}px`,
            height: `${deviceSize.height}px`,
            maxWidth: '100%',
            maxHeight: '100%',
            minWidth: '320px',
            overflow: 'auto',
            resize: isPro ? 'both' : 'none',
            bgcolor: 'background.paper',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            borderRadius: 1,
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease-in-out'
          }}
        >
          {currentPreview?.previewUrl ? (
            <iframe
              src={currentPreview.previewUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={`${clientType} preview`}
            />
          ) : (
            <iframe
              srcDoc={`
                <!DOCTYPE html>
                <html>
                  <head>
                    <meta charset="utf-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
                    <meta name="color-scheme" content="${colorScheme}">
                    <style>
                      * {
                        box-sizing: border-box;
                        margin: 0;
                        padding: 0;
                      }
                      body {
                        overflow-x: hidden;
                      }
                    </style>
                  </head>
                  <body>
                    ${transformHtmlForClient(html, clientType)}
                  </body>
                </html>
              `}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={`${clientType} preview`}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}; 